import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  ArrowRight,
  RotateCcw,
  AlertCircle,
  Frown,
  Smile,
  Meh,
  Heart,
  Zap,
  Copy,
  Shield,
  Info,
} from 'lucide-react';
import { Button, Select, Card } from '../components/ui';
import type { LanguageCode, Register } from '@zolotalk/shared-types';
import {
  createVoiceSessionClient,
  type VoiceSessionClient,
  type VoiceSessionState as ClientState,
} from '../lib/voice-session';
import { apiClient } from '../api/client';
import './VoiceSessionPage.css';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'ru', label: 'Русский' },
];

const REGISTER_OPTIONS = [
  { value: 'auto', label: 'Auto (detect from context)' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

const EMOTION_ICONS = {
  neutral: Meh,
  happy: Smile,
  sad: Frown,
  angry: Zap,
  excited: Heart,
  fearful: AlertCircle,
} as const;

const EMOTION_LABELS = {
  neutral: 'Neutral',
  happy: 'Happy',
  sad: 'Sad',
  angry: 'Angry',
  excited: 'Excited',
  fearful: 'Fearful',
} as const;

type EmotionType = keyof typeof EMOTION_ICONS;

/** Client-visible refusal codes mapped to friendly copy. */
const ERROR_COPY: Record<string, string> = {
  NOT_SIGNED_IN: 'Sign in to start a voice session',
  SESSION_LIMIT_REACHED: 'You already have the maximum number of active voice sessions',
  CONNECTION_LOST: 'Connection lost — start a new session to continue',
};

interface VoiceSessionUiState {
  status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
  isRecording: boolean;
  transcript: string;
  translation: string;
  emotion: EmotionType | null;
  audioQueue: string[];
  isPlaying: boolean;
  error: string | null;
}

function toUiStatus(state: ClientState): VoiceSessionUiState['status'] {
  switch (state) {
    case 'idle':
      return 'idle';
    case 'connecting':
    case 'reconnecting':
      return 'connecting';
    case 'active':
      return 'connected';
    case 'ended':
      return 'disconnected';
    case 'error':
      return 'error';
  }
}

export function VoiceSessionPage() {
  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode>('en');
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>('es');
  const [register, setRegister] = useState<Register>('auto');
  const [sessionState, setSessionState] = useState<VoiceSessionUiState>({
    status: 'idle',
    isRecording: false,
    transcript: '',
    translation: '',
    emotion: null,
    audioQueue: [],
    isPlaying: false,
    error: null,
  });
  const clientRef = useRef<VoiceSessionClient | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioQueueRef = useRef<HTMLAudioElement[]>([]);
  /** Serializes chunk base64 conversion so frames keep arrival order. */
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const startRecordingWhenActiveRef = useRef(false);

  const updateState = useCallback((partial: Partial<VoiceSessionUiState>) => {
    setSessionState(prev => ({ ...prev, ...partial }));
  }, []);

  const playAudio = useCallback(
    (base64Audio: string) => {
      try {
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioQueueRef.current.push(audio);

        audio.onended = () => {
          URL.revokeObjectURL(url);
          audioQueueRef.current = audioQueueRef.current.filter(a => a !== audio);
          updateState({ isPlaying: audioQueueRef.current.length > 0 });
        };

        updateState({ isPlaying: true });
        audio.play().catch(console.error);
      } catch (error) {
        console.error('Failed to play audio:', error);
      }
    },
    [updateState]
  );

  const beginCapture = useCallback(async () => {
    const client = clientRef.current;
    if (!client || client.getState() !== 'active') {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          uploadQueueRef.current = uploadQueueRef.current.then(
            () =>
              new Promise<void>(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64 = String(reader.result).split(',')[1];
                  if (base64) {
                    clientRef.current?.sendAudioChunk(base64);
                  }
                  resolve();
                };
                reader.readAsDataURL(event.data);
              })
          );
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        // Flush pending chunk uploads, then mark the utterance boundary so
        // the server runs the utterance through the pipeline.
        uploadQueueRef.current = uploadQueueRef.current.then(() => {
          clientRef.current?.endUtterance();
        });
      };

      recorder.start(1000);
      updateState({ isRecording: true, error: null });
    } catch {
      updateState({ error: 'Microphone access denied', isRecording: false });
    }
  }, [updateState]);

  const disposeClient = useCallback(() => {
    clientRef.current?.dispose();
    clientRef.current = null;
  }, []);

  const createClient = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const client = createVoiceSessionClient({
      url: `${protocol}//${window.location.host}/ws/voice`,
      config: { sourceLanguage, targetLanguage, register },
      getAccessToken: () => apiClient.getValidAccessToken(),
      callbacks: {
        onStateChange: state => {
          updateState({ status: toUiStatus(state) });
          if (state === 'active' && startRecordingWhenActiveRef.current) {
            startRecordingWhenActiveRef.current = false;
            void beginCapture();
          }
          if (state === 'ended' || state === 'error') {
            mediaRecorderRef.current?.stop();
            updateState({ isRecording: false });
          }
        },
        onTranslation: event => {
          updateState({
            transcript: event.transcript,
            translation: event.translation,
            emotion: (event.emotion as EmotionType | undefined) ?? 'neutral',
          });
          if (event.audioBase64) {
            playAudio(event.audioBase64);
          }
        },
        onSessionEnded: reason => {
          if (reason === 'max_duration') {
            updateState({ error: 'Session reached its time limit' });
          }
        },
        onError: (code, message) => {
          updateState({ error: ERROR_COPY[code] ?? message });
        },
      },
    });
    clientRef.current = client;
    return client;
  }, [sourceLanguage, targetLanguage, register, updateState, beginCapture, playAudio]);

  const startRecording = useCallback(() => {
    const existing = clientRef.current;
    if (existing && existing.getState() === 'active') {
      void beginCapture();
      return;
    }
    // No live session: start one (auth happens in-band on connect) and
    // begin capturing as soon as the server confirms it.
    if (existing) {
      disposeClient();
    }
    startRecordingWhenActiveRef.current = true;
    updateState({ error: null });
    createClient().start();
  }, [beginCapture, createClient, disposeClient, updateState]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    updateState({ isRecording: false });
  }, [updateState]);

  const toggleRecording = useCallback(() => {
    if (sessionState.isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [sessionState.isRecording, startRecording, stopRecording]);

  const swapLanguages = useCallback(() => {
    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
    if (sessionState.transcript) {
      updateState({ transcript: sessionState.translation, translation: sessionState.transcript });
    }
  }, [
    sourceLanguage,
    targetLanguage,
    sessionState.transcript,
    sessionState.translation,
    updateState,
  ]);

  const handleCopyTranscript = useCallback(async () => {
    if (sessionState.transcript) {
      await navigator.clipboard.writeText(sessionState.transcript);
    }
  }, [sessionState.transcript]);

  const handleCopyTranslation = useCallback(async () => {
    if (sessionState.translation) {
      await navigator.clipboard.writeText(sessionState.translation);
    }
  }, [sessionState.translation]);

  const clearSession = useCallback(() => {
    stopRecording();
    clientRef.current?.endSession();
    disposeClient();
    updateState({
      status: 'idle',
      transcript: '',
      translation: '',
      emotion: null,
      error: null,
    });
    audioQueueRef.current.forEach(a => {
      a.pause();
      a.currentTime = 0;
    });
    audioQueueRef.current = [];
  }, [stopRecording, disposeClient, updateState]);

  // A session is bound to its start config: changing languages or register
  // ends the current session; the next recording starts a fresh one.
  useEffect(() => {
    if (clientRef.current) {
      mediaRecorderRef.current?.stop();
      clientRef.current.endSession();
      disposeClient();
      updateState({ status: 'idle', isRecording: false });
    }
  }, [sourceLanguage, targetLanguage, register, disposeClient, updateState]);

  useEffect(() => {
    return () => {
      clientRef.current?.dispose();
      mediaRecorderRef.current?.stop();
      audioQueueRef.current.forEach(a => {
        a.pause();
        a.currentTime = 0;
      });
    };
  }, []);

  const EmotionIcon = sessionState.emotion ? EMOTION_ICONS[sessionState.emotion] : Meh;
  const emotionLabel = sessionState.emotion ? EMOTION_LABELS[sessionState.emotion] : 'Neutral';

  return (
    <div className="page voice-session-page">
      <div className="container">
        <header className="page-header">
          <h1>Voice Translation</h1>
          <p className="page-description">
            Real-time voice translation with emotion mirroring and register awareness.
          </p>
        </header>

        <div className="voice-session-grid">
          <section className="voice-controls-section" aria-labelledby="controls-heading">
            <h2 id="controls-heading" className="visually-hidden">
              Voice Controls
            </h2>

            <Card className="config-card">
              <div className="config-header">
                <h3>Session Configuration</h3>
                <span
                  className={`connection-status ${sessionState.status === 'connected' ? 'connected' : ''}`}
                  aria-live="polite"
                >
                  <span className="status-dot" aria-hidden="true" />
                  {sessionState.status === 'connected' ? 'Connected' : sessionState.status}
                </span>
              </div>

              <div className="config-grid">
                <Select
                  id="voice-source-language"
                  label="Source Language"
                  value={sourceLanguage}
                  onChange={e => setSourceLanguage(e.target.value as LanguageCode)}
                  options={LANGUAGE_OPTIONS}
                />

                <button
                  type="button"
                  className="swap-button"
                  onClick={swapLanguages}
                  aria-label="Swap source and target languages"
                  disabled={sessionState.isRecording}
                >
                  <ArrowRight size={20} />
                </button>

                <Select
                  id="voice-target-language"
                  label="Target Language"
                  value={targetLanguage}
                  onChange={e => setTargetLanguage(e.target.value as LanguageCode)}
                  options={LANGUAGE_OPTIONS}
                />
              </div>

              <Select
                id="voice-register"
                label="Register / Tone"
                value={register}
                onChange={e => setRegister(e.target.value as Register)}
                options={REGISTER_OPTIONS}
              />
            </Card>

            <Card className="mic-card">
              <div className="mic-visualizer" aria-hidden="true">
                <div
                  className={`mic-ring ${sessionState.isRecording ? 'recording' : ''} ${sessionState.isPlaying ? 'playing' : ''}`}
                />
                <button
                  type="button"
                  className={`mic-button ${sessionState.isRecording ? 'recording' : ''}`}
                  onClick={toggleRecording}
                  disabled={sessionState.status === 'connecting'}
                  aria-label={sessionState.isRecording ? 'Stop recording' : 'Start recording'}
                  aria-pressed={sessionState.isRecording}
                >
                  {sessionState.isRecording ? <MicOff size={32} /> : <Mic size={32} />}
                </button>
              </div>

              <p className="mic-status" aria-live="polite">
                {sessionState.isRecording
                  ? 'Listening...'
                  : sessionState.status === 'connecting'
                    ? 'Connecting...'
                    : sessionState.status === 'connected'
                      ? 'Tap to start speaking'
                      : sessionState.status === 'error'
                        ? 'Connection error'
                        : 'Tap the mic to start a session'}
              </p>

              {sessionState.error && (
                <div className="error-banner" role="alert">
                  <AlertCircle size={16} />
                  <span>{sessionState.error}</span>
                </div>
              )}
            </Card>

            <div className="action-buttons">
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={clearSession}
                disabled={!sessionState.transcript && !sessionState.translation}
              >
                <RotateCcw size={20} />
                <span>Clear Session</span>
              </Button>
            </div>
          </section>

          <section className="translation-results-section" aria-labelledby="results-heading">
            <h2 id="results-heading" className="visually-hidden">
              Translation Results
            </h2>

            <Card className="result-card transcript-card">
              <div className="result-header">
                <h3>
                  <span className="language-badge">{sourceLanguage.toUpperCase()}</span>
                  Transcript
                </h3>
                <div className="result-actions">
                  {sessionState.transcript && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyTranscript}
                      aria-label="Copy transcript"
                    >
                      <Copy size={16} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="result-content">
                {sessionState.transcript ? (
                  <p className="transcript-text">{sessionState.transcript}</p>
                ) : (
                  <p className="placeholder-text">Your speech will appear here...</p>
                )}
              </div>
            </Card>

            <Card className="result-card translation-card">
              <div className="result-header">
                <h3>
                  <span className="language-badge">{targetLanguage.toUpperCase()}</span>
                  Translation
                </h3>
                <div className="result-actions">
                  {sessionState.translation && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyTranslation}
                      aria-label="Copy translation"
                    >
                      <Copy size={16} />
                    </Button>
                  )}
                  {sessionState.translation && sessionState.audioQueue.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {}}
                      aria-label={sessionState.isPlaying ? 'Pause playback' : 'Play translation'}
                    >
                      {sessionState.isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </Button>
                  )}
                </div>
              </div>
              <div className="result-content">
                {sessionState.translation ? (
                  <p className="translation-text">{sessionState.translation}</p>
                ) : (
                  <p className="placeholder-text">Translation will appear here...</p>
                )}
              </div>
            </Card>

            {sessionState.emotion && (
              <Card className="emotion-card">
                <div className="emotion-header">
                  <EmotionIcon size={24} className="emotion-icon" />
                  <span className="emotion-label">Detected Emotion: {emotionLabel}</span>
                </div>
                <p className="emotion-description">
                  The translation mirrors the detected emotional tone in the output audio.
                </p>
              </Card>
            )}
          </section>
        </div>

        <div className="ai-disclosure" role="status" aria-live="polite">
          <Shield size={16} />
          <span>
            Translations are AI-generated. Audio uses synthetic voices — no voice cloning.
          </span>
          <Info size={16} />
        </div>
      </div>
    </div>
  );
}
